
import {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {Controller} from 'react-hook-form';
import dayjs from 'dayjs';

import SelectInput from '../../../../../components/ui/Inputs/SelectInput/SelectInput';
import TextInput from '../../../../../components/ui/Inputs/TextInput';
import DropdownInput from '../../../../../components/ui/Inputs/DropdownInput';
import TextAreaInput from '../../../../../components/ui/Inputs/TextAreaInput';
import DateInput from '../../../../../components/ui/Inputs/DateInput/DateInput';
import Button from '../../../../../components/ui/Button/Button';
import {notify} from '../../../../../components/ui/Toast/Toast';
import {ProductionCategoryActions, DealActions, ProductionActions} from '../../../../../actions';
import {frenchFormat, fetchSelectData} from '../../../../../utils';
import Error from '../../../../../components/ui/Error/Error';
import {getErrors, standardFormat} from '../../../../../utils';
import ContainerCard from '../../../../../components/ui/ContainerCard/ContainerCard';

function CreateNewProduction({
    userData,
    switchForm,
    setSwitchForm,
    setView,
    productionToBeModified,
    refreshData
}) {
    const [isDisabled, setIsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [categories, setCategories] = useState(null);
    const [deals, setDeals] = useState(null);

    const newProduction = [{name: 'Production'}, {name: 'Congés'}];

    // Check date value for new expense from calendar popup
    const defaultDate = (productionToBeModified?.date && !productionToBeModified?.id) ? dayjs(productionToBeModified.date) : dayjs();

    const defaultValues = {
        date: defaultDate,
        deal: null,
        category: '',
        location: '',
        produced_days: null,
        billed_days: null,
        mission: null,
        notes: '',
        work_time_status: null
    };

    // -- functions that get data for Select Inputs --
    const handleGetCategories = async (search, page = 1) => {
        await fetchSelectData({action: ProductionCategoryActions.getAllProductionCategories, search:`&search=${search}`, page: page, setter: setCategories, userData});
    };

    const handleGetDeals = async (search, page) => {
        await fetchSelectData({action: DealActions.getAllDeals, search:`&search=${search}&display=${userData.userProfileName}&include_internal=true`, page: page, setter: setDeals, userData, resultPath: 'data'});
    };


    useEffect(() => {
        (async () => {
            await handleGetCategories('', 1);
        })();
        (async () => {
            await handleGetDeals('', 1);
        })();
    }, []);

    const {handleSubmit, control, reset, setValue, getValues, formState: {isValid}} = useForm({defaultValues: defaultValues});
    const hydratedDealProductionIdRef = useRef(null);

    const workTimeStatus = [{
        label: "Réalisé",
        value: false
    }, {
        label: "Prévisionnel",
        value: true
    }]

    // Fonction de formatage du label des affaires
    // Affiche seulement short_name pour les affaires internes (type activity ou project)
    const formatDealLabel = (deal) => {
        const isInternal = deal.type === 'activity' || deal.type === 'project';
        if (isInternal) {
            return deal.short_name;
        }
        return `${deal.customer?.name} - ${deal.short_name}`;
    };

    useEffect(() => {
        if (!productionToBeModified?.id) {
            hydratedDealProductionIdRef.current = null;

            return;
        }

        const dealObject = typeof productionToBeModified?.deal === 'number' ?
            null :
            productionToBeModified?.deal;

        hydratedDealProductionIdRef.current = typeof productionToBeModified?.deal === 'number' ? null : productionToBeModified.id;

        reset({
            date: productionToBeModified?.date,
            deal: dealObject,
            category: {name: productionToBeModified?.category},
            location: productionToBeModified?.location,
            produced_days: (+productionToBeModified?.produced_days).toString(),
            billed_days: (+productionToBeModified?.billed_days).toString(),
            notes: productionToBeModified?.notes,
            work_time_status: productionToBeModified?.is_forecasted ? workTimeStatus[1] : workTimeStatus[0]
        });
    }, [productionToBeModified?.id]);

    // Hydrate only the async deal select; resetting the full form here would wipe user edits.
    useEffect(() => {
        if (
            !productionToBeModified?.id ||
            hydratedDealProductionIdRef.current === productionToBeModified.id ||
            typeof productionToBeModified?.deal !== 'number'
        ) {
            return;
        }

        const dealObject = deals?.options?.find(deal => deal.id === productionToBeModified.deal);

        if (!dealObject || getValues('deal')) {
            return;
        }

        setValue('deal', dealObject, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: true
        });
        hydratedDealProductionIdRef.current = productionToBeModified.id;
    }, [deals, getValues, productionToBeModified?.deal, productionToBeModified?.id, setValue]);

    const onSubmit = async (data) => {
        setErrors(null);
        setIsDisabled(true);
        setIsLoading(true);

        const production = {
            date: standardFormat(data.date),
            deal_id: data.deal.id,
            category: data.category.name,
            location: data.location,
            produced_days: data.produced_days,
            billed_days: data.billed_days,
            notes: data.notes,
            ressource_id: userData.userId,
            is_forecasted: data.work_time_status.value
        };

        if (!productionToBeModified || productionToBeModified.date && Object.keys(productionToBeModified).length === 1) {
            const result = await ProductionActions.createProduction(userData, production);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Votre temps à bien été créé');
                setView(false);
            }
        }
        else {
            const result = await ProductionActions.updateProduction(userData, productionToBeModified?.id, production);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('Votre temps à bien été modifié');
                setView(false);
            }
        }

        setIsLoading(false);
        setIsDisabled(false);
    };

    return (
        <ContainerCard
            title={productionToBeModified?.id ? 'Éditer un temps' : 'Nouveau temps'}
            coloredButtonDisplayed={false}
            overflow=''
        >
            <div className="px-4">
                <Controller
                    name="type"
                    control={control}
                    render={({
                        field: {onChange, value},
                        fieldState: {error}
                    }) => (
                        <DropdownInput
                            label={'Type de temps'}
                            onChange={onChange}
                            setSwitchForm={setSwitchForm}
                            value={switchForm ? switchForm : value}
                            error={error}
                            required={true}
                            items={newProduction}
                            disabled={(productionToBeModified && productionToBeModified.id) ? true : isDisabled}
                            listHeight="70px"
                        />
                    )}
                />
            </div>
            <div className="px-4 pt-2">
                <Controller
                    name="deal"
                    control={control}
                    rules={{required: {
                        value: true,
                        message: 'Veuillez sélectionner une affaire.'
                    }}}
                    render={({
                        field: {onChange, value, ref},
                        fieldState: {error}
                    }) => (
                        <SelectInput
                            label='Affaire'
                            placeholder='Affaire'
                            inputRef={ref}
                            value={value}
                            error={error}
                            options={deals?.options || []}
                            hasNextPage={deals?.hasNextPage || false}
                            fetchFunction={handleGetDeals}
                            loading={!deals}
                            onChange={onChange}
                            labelKeys={['customer.name', 'short_name']}
                            labelFormatter={formatDealLabel}
                            isSearchable={true}
                            required={true}
                            disabled={isDisabled}
                            width="w-full"
                            labelWidth="w-[170px]"
                            separator=' - '
                        />
                    )}
                />
            </div>
            <div className="flex flex-row justify-between px-4 pt-2 pb-4">
                <div className='flex flex-col'>

                    <div className="mb-2">
                        <Controller
                            name="date"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner une date.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <DateInput
                                    label={'Date'}
                                    onChange={onChange}
                                    value={frenchFormat(value)}
                                    error={error}
                                    required={true}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="category"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un Type de tâche.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Type de tâche'
                                    placeholder='Type de tâche'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={categories?.options.sort((a, b) => a.id - b.id) || []}
                                    hasNextPage={categories?.hasNextPage || false}
                                    onChange={onChange}
                                    loading={!categories}
                                    isSearchable={true}
                                    fetchFunction={handleGetCategories}
                                    labelKeys={['name']}
                                    required={true}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                    </div>

                    <div className="mb-2">
                        <Controller
                            name="produced_days"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Veuillez indiquer un nombre de jours produits.'
                                }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Jours produits (j)'}
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={true}
                                    disabled={isDisabled}
                                    min="0"
                                    placeholder="Jours produits (j)"
                                    type="number"
                                />
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col justify-between">
                    <div className='mb-2'>
                        <Controller
                            name="work_time_status"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un type de saisie.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Type de saisie'
                                    placeholder='Type de saisie'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={workTimeStatus}
                                    onChange={onChange}
                                    isSearchable={true}
                                    labelKeys={['label']}
                                    required={true}
                                    disabled={isDisabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="location"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Veuillez indiquer une localisation.'
                                }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label="Emplacement"
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={true}
                                    disabled={isDisabled}
                                    placeholder="Localisation"
                                    type="text"
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="billed_days"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Veuillez indiquer un nombre de jours facturés.'
                                }
                            }}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Jours facturés (j)'}
                                    onChange={onChange}
                                    value={value}
                                    error={error}
                                    required={true}
                                    disabled={isDisabled}
                                    min="0"
                                    placeholder="Jours facturés (j)"
                                    type="number"
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
            <div className="py-4 mx-4 border-t border-borderGrey">
                <Controller
                    name="notes"
                    control={control}
                    render={({
                        field: {onChange, value}
                    }) => (
                        <TextAreaInput
                            placeholder={'Tapez votre texte'}
                            maxLength={100}
                            onChange={onChange}
                            value={value}
                            isDisabled={isDisabled}
                        />
                    )}
                />
            </div>
            <div className="flex flex-col justify-center pb-2">
                <div className="flex justify-center pb-2">
                    <Button
                        onClick={() => setView(false)}
                        type={'secondary'}
                        content={'Annuler'}
                        disabled={isDisabled}
                    />

                    <Button
                        type={'primary'}
                        content={productionToBeModified?.id ? 'Modifier le temps' : 'Ajouter le temps'}
                        onClick={handleSubmit(onSubmit)}
                        loading={isLoading}
                        disabled={!categories || !deals || !isValid}
                    />

                </div>
                <Error errors={errors}/>
            </div>
        </ContainerCard>
    );
}

export default CreateNewProduction;
